import * as R from 'ramda';
import memoizeOne from 'memoize-one';
import { FixedSizeList as List } from 'react-window';
import React, { useMemo, forwardRef, createContext } from 'react';
// utilities
import * as U from '../../utilities';
// feature load-boards
import ResultRow from './components/result-row';
import ResultsHeader from './components/results-header';
////////////////////////////////////////////////

const StickyListContext = createContext();
StickyListContext.displayName = 'StickyListContext';

const ItemWrapper = memoizeOne(({
  data,
  index,
  style,
}) => {
  const {
    page,
    size,
    listLength,
    ItemRenderer,
    stickyIndices,
    onNextPageLoad,
  } = data;

  if (stickyIndices.includes?.(index)) {
    return null;
  }

  const itemIndex = U.ifElse(R.equals(size, 50), index, R.multiply(index, R.add(page, 1)));

  if (R.equals(itemIndex, listLength)) onNextPageLoad();

  return <ItemRenderer style={style} index={index} />;
});

const innerElementType = forwardRef(({ children, ...rest }, ref) => (
  <StickyListContext.Consumer>
    {({
      order,
      orderBy,
      itemSize,
      handleSort,
      stickyIndices,
      columnSettings,
    }) => (
      <div ref={ref} {...rest}>
        {stickyIndices.map(index => (
          <ResultsHeader
            order={order}
            orderBy={orderBy}
            itemSize={itemSize}
            handleSort={handleSort}
            columnSettings={columnSettings}
            key={`virtualized-table-header-${index}`}
            style={{
              top: 0,
              left: 0,
              width: '100%',
              height: {itemSize},
            }}
          />
        ))}

        {children}
      </div>
    )}
  </StickyListContext.Consumer>
));

const StickyList = props => {
  const {
    page,
    size,
    order,
    orderBy,
    children,
    handleSort,
    listLength,
    stickyIndices,
    columnSettings,
    onNextPageLoad,
    ...rest
  } = props;

  const { itemSize } = rest;

  const value = useMemo(() => ({
    order,
    orderBy,
    itemSize,
    handleSort,
    stickyIndices,
    columnSettings,
    ItemRenderer: children,
  }), [order, orderBy, itemSize, handleSort, children, stickyIndices, columnSettings]);

  return (
    <StickyListContext.Provider value={value}>
      <List
        itemData={{
          page,
          size,
          listLength,
          stickyIndices,
          onNextPageLoad,
          ItemRenderer: children,
        }}
        {...rest}
      >
        {ItemWrapper}
      </List>
    </StickyListContext.Provider>
  );
};

const VirtualizedTable = ({
  page,
  size,
  list,
  width,
  order,
  height,
  orderBy,
  itemSize,
  itemCount,
  deletedList,
  handlers = {},
  columnSettings,
  isBookmarkTable,
  notAllowActions,
  deletedPropCheck,
  userLoadBoardConfigs,
}) => {
  const handleSort = R.or(handlers.handleSort, () => {});
  const onNextPageLoad = R.or(handlers.onNextPageLoad, () => {});

  return (
    <StickyList
      page={page}
      size={size}
      width={width}
      order={order}
      height={height}
      orderBy={orderBy}
      itemSize={itemSize}
      stickyIndices={[-1]}
      itemCount={itemCount}
      handleSort={handleSort}
      columnSettings={columnSettings}
      listLength={R.dec(R.length(list))}
      innerElementType={innerElementType}
      style={{ fontFamily: 'Urbanist, serif' }}
      onNextPageLoad={U.debounce(onNextPageLoad)}
    >
      {props => {
        const { index, style } = props;

        const resultRowStyle = R.assoc(
          'top',
          R.add(itemSize, U.getPropFromObject('top', style)),
          style,
        );

        const data = list[index];
        const deleted = U.isTrue(R.path([R.prop(deletedPropCheck, data)], deletedList));

        if (!data) return null;

        return (
          <ResultRow
            data={data}
            deleted={deleted}
            handlers={handlers}
            itemSize={itemSize}
            style={resultRowStyle}
            columnSettings={columnSettings}
            isBookmarkTable={isBookmarkTable}
            notAllowActions={notAllowActions}
            userLoadBoardConfigs={userLoadBoardConfigs}
          />
        );
      }}
    </StickyList>
  );
};

export default VirtualizedTable;
